<template>
  <div class="subscribe-content" :class="props.noScroll ? 'subscribe-content--no-scroll' : ''">
    <div class="subscribe-content__wrapper">
      <!-- Header Section -->
      <header class="subscribe-content__header">
        <div class="subscribe-content__brand">
          <LogotypeCapital class="subscribe-content__logo" />
          <CaplsuleLegend v-if="isLegendSectionOpened" />
          <span class="subscribe-content__badge" :class="isGiftWidget ? 'subscribe-content__badge--gift' : ''" v-else>
            Подписка
          </span>
        </div>
        <p class="subscribe-content__description">{{ getDescription }}</p>
      </header>

      <!-- Legend Section -->
      <template v-if="isLegendSectionOpened">
        <SubscribeItem
          v-if="legendSubscription"
          :price="legendSubscription.price"
          :value="legendSubscription.value"
          :currency="legendSubscription.currency"
          :days="legendSubscription.months"
          :tagStyle="'default'"
          :isActive="true"
        />
      </template>

      <!-- Regular Subscriptions -->
      <template v-else-if="!isGiftWidget">
        <section class="subscribe-content__plans">
          <template v-for="sub in subscriptions">
            <SubscribeItem
              :key="sub.id"
              :price="sub.price"
              :value="sub.value"
              :currency="sub.currency"
              :days="sub.months"
              :tag="sub.tag"
              :tagStyle="sub.tagStyle"
              :isPon="sub.isPon"
              :isActive="selectedId == sub.id"
              @click="selectSubscription(sub.id)"
              v-if="!sub.isPon || (sub.isPon && !isGiftWidget)"
            />
          </template>
        </section>
      </template>

      <!--Дополнительные подарочные подписки-->
      <template v-if="isGiftWidget && subscriptionsGifts">
        <section class="subscribe-content__plans">
          <SubscribeItem
            v-for="sub in subscriptionsGifts.user_1"
            :key="sub.id"
            :price="sub.price"
            :value="sub.value"
            :currency="sub.currency"
            :days="sub.months"
            :tag="sub.tag"
            :tagStyle="sub.tagStyle"
            :isPon="sub.isPon"
            :isActive="selectedId == sub.id"
            @click="selectSubscription(sub.id)"
          />
        </section>
      </template>

      <!-- Benefits Grid -->
      <section class="subscribe-content__benefits">
        <SubscriptionBenefitsItem
          v-for="item in filteredBenefits"
          :key="item.iconName"
          class="subscribe-content__benefit-item"
          :title="item.title"
          :iconName="item.iconName"
          :text="item.description"
          :isLegend="item.isLegend"
          :isLegendColor="item.isLegendColor"
        />
      </section>
    </div>

    <!-- Actions Footer -->
    <footer class="subscribe-content__footer">
      <!-- Legend Payment Button -->
      <template v-if="isLegendSectionOpened && legendSubscription">
        <div class="subscribe-content__footer-box">
          <button
            v-if="!paymentUrl"
            class="subscribe-content__legend-btn"
            :style="`--bg-legend: url('${BG_LEGEND}')`"
            @click="activeLegendary()"
          >
            Стать Легендой на 12 месяцев
          </button>
          <a
            v-else
            class="subscribe-content__legend-btn"
            :style="`--bg-legend: url('${BG_LEGEND}')`"
            :href="paymentUrl"
            target="_blank"
          >
            Перейти к оплате
        </a>
        </div>
      </template>

      <!-- PoN -->
      <template v-else-if="selectedSubscription.isPon">
        <div class="subscribe-content__marketplace-buttons">
          <ButtonMain
            class="subscribe-content__marketplace-btn"
            :link="selectedSubscription.link"
            type="blue-border"
            target="_blank"
            width="100%"
          >
            Купить на OpenSea <LinkIcon class="subscribe-content__link-icon" />
          </ButtonMain>
          <ButtonMain
            class="subscribe-content__marketplace-btn"
            link="https://blur.io/eth/collection/proof-of-narnian-nft-og"
            type="blue-border"
            target="_blank"
            width="100%"
          >
            Купить на Blur <LinkIcon class="subscribe-content__link-icon" />
          </ButtonMain>
        </div>
        <ButtonMain
          class="subscribe-content__marketplace-btn"
          type="primary"
          @click="handlePonActivation"
          width="100%"
        >
          Активировать PoN
        </ButtonMain>
      </template>

      <template v-else>
        <template v-if="paymentUrl">
          <ButtonMain 
            class="subscribe-content__pay-btn" 
            :type="isGiftWidget ? 'blue-gradient' : 'primary'" 
            :is="'a'" 
            :link="paymentUrl" 
            :width="isGiftWidget ? 'fit-content' : '100%'"
            :target="'_blank'"
          >
            Перейти к оплате
          </ButtonMain>
        </template>
        <template v-else>
          <ButtonMain
            class="subscribe-content__pay-btn"
            :type="isGiftWidget ? 'blue-gradient' : 'primary'" 
            :is="'button'"
            :width="isGiftWidget ? 'fit-content' : '100%'"
            @click="handleSubscriptionPayment"
          >
            {{ buttonText }}
          </ButtonMain>
        </template>
      </template>
    </footer>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onBeforeUnmount } from "vue";
  import { useStore } from "vuex";
  import { useRouter } from "vue-router";
  import { PATHS_NAME } from "@shared/routers/consts/pathsName.js";
  import { backendUrl } from "@shared/constants/urls/appUrls";
  import { fetchWithAuth } from "@shared/utils/auth";
  import DROPDOWN_NAMES from "@shared/constants/dropdowns/dropdownsName.js";
  import AUTH_SECTIONS_NAME from "@shared/constants/dropdowns/authSectionsName.js";



  //---------------------------------------------------------
  // TEMPLATES
  //---------------------------------------------------------
  import SubscribeItem from "./SubscribeItem/SubscribeItem.vue";
  import ButtonMain from "@shared/UI/Buttons/ButtonMain/ButtonMain.vue";
  import SubscriptionBenefitsItem from "./SubscriptionBenefitsItem/SubscriptionBenefitsItem.vue";
  import LogotypeCapital from "@shared/UI/Logotypes/LogotypeCapital/LogotypeCapital.vue";
  import LinkIcon from "@shared/UI/SVG/LinkIcon.vue";
  import CaplsuleLegend from "@shared/UI/Capsules/CaplsuleLegend/CaplsuleLegend.vue";



  //---------------------------------------------------------
  // MACRO
  //---------------------------------------------------------
  const store = useStore();
  const router = useRouter();
  const emit = defineEmits();
  const props = defineProps({
    isGiftMode: { type: Boolean, default: false },
    noScroll: { type: Boolean, default: false }
  })



  //---------------------------------------------------------
  // CONSTANTS
  //---------------------------------------------------------
  const BG_LEGEND = require("@shared/images/backgrounds/legend_bg.png");
  const BENEFITS = [
    { iconName: "alpha", title: "Альфа", description: "Рыночная аналитика, анализ проектов, инвест идеи, разбор ICO, и т.д." },
    { iconName: "podcasts", title: "Подкасты", description: "Три подкаста в неделю с фаундерами Drops Capital и уникальными гостями" },
    { iconName: "calendar", title: "Календарь", description: "Отмечаем все важные события крипторынка и мира" },
    { iconName: "forecasters", title: "Форкастеры", description: "Топовые голоса твиттера и телеграма на русском в удобном формате" },
    { iconName: "signal", title: "Сигналы", description: "Фиксируем идеи и их движения" },
    { iconName: "screener", title: "Скринер", description: "Отслеживаем динамику всех крипто-активов" },
    { iconName: "work", title: "Кэпитал Work", description: "Рабочий чат - минимум флуда, максимум пользы" },
    { iconName: "meme", title: "Кэпитал Meme", description: "Торговля и обсуждение мемкоинов" },
    { iconName: "alerts", title: "Кэпитал Алертс", description: "Канал с сигналами от коммьюнити" },
    { iconName: "holders", title: "PoN Holders", description: "Эксклюзивный чат для владельцев PoN NFT" },
    { iconName: "podcasts", title: "+1 Подкаст", description: "Дополнительные оффтоп подкасты и контент от Сергея с командой", isLegend: true },
    { iconName: "crown", title: "Легендарный никнейм", description: "Отметим вашу щедрость и особый статус на Кэпитале", isLegend: true, isLegendColor: true },
  ];
  const PON_OBJECT = {
    id: "pon",
    price: "0.34",
    value: "Разово",
    currency: "ETH",
    isPon: true,
    link: "https://opensea.io/collection/proof-of-narnian",
    tagStyle: "default",
  }



  //---------------------------------------------------------
  // REFS
  //---------------------------------------------------------
  const selectedId = ref(null);
  const paymentUrl = ref(null);
  //---------------------------------------------------------



  //---------------------------------------------------------
  // LIFECYCLE
  //---------------------------------------------------------
  onMounted(async () => {
    if (!storeTariffs.value.length) {
      await store.dispatch("HeaderSystem/fetchCryptoCloudTariffs");
    }
    const defaultSub = subscriptions.value.find((s) => s.months === 12 && !s.isPon);
    if(isGiftWidget.value){
      selectedId.value = subscriptionsGifts.value.user_1[0]?.id;
    }else{
      selectedId.value = defaultSub ? defaultSub.id : subscriptions.value[0]?.id || null;
    }
  });

  onBeforeUnmount(() => {
    sessionStorage.removeItem("isPonPageMove");
  });

  
  //---------------------------------------------------------
  // COMPUTED
  //---------------------------------------------------------
  //Это виджет поарочных подписок
  const isGiftWidget = computed(() => props.isGiftMode || store.getters["HeaderSystem/isGiftSection"]);
  //Получаем тарифы из стора
  const storeTariffs = computed(() => store.getters["HeaderSystem/tariffs"] || []);

  //Открыт ли виджет для покупки легендарной подписки?
  const isLegendSectionOpened = computed(() => store.getters["HeaderSystem/isLegendSection"]);

  //Авторизирован ли пользователь
  const isAuth = computed(() => store.getters["UserSystem/isAuthenticated"]);

  //Фильтруем legend тариф
  const legendSubscription = computed(() => {
    const t = storeTariffs.value.find((tariff) => tariff.code === "legend");
    if (!t) return null;
    const currency = t.currency === "USD" ? "$" : t.currency;
    const value = t.months && t.months > 0 ? `${(parseFloat(t.amount) / t.months).toFixed(2)} ${currency}/мес` : `${t.amount} ${currency}`;
    return { price: t.amount, value, currency, months: t.months || 12 };
  });

  //Подписки без legend и без Gifts
  const subscriptions = computed(() => {
    const mapped = (storeTariffs.value || [])
      .filter((t) => t.code !== "legend")
      .filter((t) => !t.is_gift_package)
      .map((t) => {
        const currency = t.currency === "USD" ? "$" : t.currency;
        const tag = t.code === "1year" ? "экономия 15%" : undefined;
        const tagStyle = t.code === "1year" ? "primary" : t.code === "2months" ? "secondary" : "default";
        const value = t.months && t.months > 0 ? `${(parseFloat(t.amount) / t.months).toFixed(2)} ${currency}/мес` : `${t.amount} ${currency}`;
        return { id: t.id, price: t.amount, value, currency, months: t.months, tag, tagStyle, tariff: t.code };
      });
    const sorted = mapped.sort((a, b) => b.months - a.months);
    return sorted.concat(PON_OBJECT);
  });

  //Подписки подарочные
  const subscriptionsGifts = computed(() => {
    const sub = {
      user_1: [],
      user_5: [],
    }
    if(!storeTariffs.value.length) return;
    storeTariffs.value.forEach(t => {
      if(!t.is_gift_package) return;
      t.gift_codes_count === 1 ? sub.user_1.push(t) : sub.user_5.push(t);
    })
    sub.user_1 = sub.user_1.sort((a, b) => b.months - a.months);
    const mappedUser_1 = sub.user_1.map((t) => {
      const currency = t.currency === "USD" ? "$" : t.currency;
      const tag = t.code === "1gift1year" ? "экономия 15%" : undefined;
      const tagStyle = t.code === "1gift1year" ? "primary" : '';
      const giftsCount = t.gift_codes_count;
      const value = t.months && t.months > 0 ? `${(parseFloat(t.amount) / t.months).toFixed(2)} ${currency}/мес` : `${t.amount} ${currency}`;
      return { id: t.id, price: t.amount, currency, months: t.months, tag, tagStyle, tariff: t.code, value, giftsCount, isGift: true };
    });
    sub.user_5 = sub.user_5.sort((a, b) => b.months - a.months);
    const mappedUser_5 = sub.user_5.map((t) => {
      const currency = t.currency === "USD" ? "$" : t.currency;
      let tag = undefined;
      if(t.code === "5gifts1year") tag = 'боярский подгон';
      if(t.code === "5gifts2months") tag = 'всем близким';
      const giftsCount = t.gift_codes_count;
      const tagStyle = "blue-gradient";
      return { id: t.id, price: t.amount, currency, months: t.months, tag, tagStyle, tariff: t.code, giftsCount, isGift: true };
    });
    return { user_1: mappedUser_1, user_5: mappedUser_5 };
  });

  //Получить описание попапа  
  const getDescription = computed(() => {
    if(isLegendSectionOpened.value) return 'Для тех, кто хочет сказать спасибо и помочь расти дальше';
    else if(isGiftWidget.value) return 'Подарите близким эксклюзивный контент Кэпитал. Вы получите персональные коды активации – просто отправьте их получателям подарка';
    return 'Выберите подписку на удобный срок и получите доступ к контенту Кэпитала:';
  });

  //Выбранная подписка
  const selectedSubscription = computed(() => {
    const id = selectedId.value;
    const regularSub = subscriptions.value.find((s) => s.id == id);
    if (regularSub) return regularSub;
    if (subscriptionsGifts.value?.user_1) {
      const giftSub1 = subscriptionsGifts.value.user_1.find((s) => s.id == id);
      if (giftSub1) return giftSub1;
    }
    if (subscriptionsGifts.value?.user_5) {
      const giftSub5 = subscriptionsGifts.value.user_5.find((s) => s.id == id);
      if (giftSub5) return giftSub5;
    }
    return {};
  });

  //Фильтр для бенефитов (в зависимости от виджета, разные отображаются)
  const filteredBenefits = computed(() => {
    if (isLegendSectionOpened.value) {
      return BENEFITS.filter((item) => item.isLegend);
    }
    if (selectedSubscription.value.isPon) {
      return BENEFITS.filter((item) => !item.isLegend);
    }
    return BENEFITS.filter((item) => !item.isLegend && item.iconName !== "holders");
  });

  //Текст кнопки
  const buttonText = computed(() => {
    const sub = selectedSubscription.value;
    if (sub?.isPon) return "Активировать PoN";
    if (sub?.isGift){
      if(sub.giftsCount === 1) return `Купить подписку в подарок на ${sub.months} ${pluralizeMonths(sub.months)}`;
      else return `Купить ${sub.giftsCount} подписок в подарок на ${sub.months} ${pluralizeMonths(sub.months)}`
    }
    if (sub?.months && !isNaN(sub.months)) return `Купить подписку на ${sub.months} ${pluralizeMonths(sub.months)}`;
    return "Оформить подписку";
  });

  //---------------------------------------------------------
  //Выбираем подписку
  const selectSubscription = (id) => {
    selectedId.value = id;
    sessionStorage.setItem("telegramWidgetCurrentID", String(id));
  };

  //Получаем ссылку для оплаты: делаем автопереход по ссылке и записываем ее в переменную, чтобы можно было перейти через кнопку
  const createCryptoCloudPayment = async (tariff) => {
    const url = `${backendUrl}/api/payments/cryptocloud/create/`;
    const formData = new FormData();
    formData.append("tariff", tariff);
    try {
      const response = await fetchWithAuth(url, { method: "POST", body: formData });
      if (!response.ok) throw new Error("Ошибка при создании оплаты");
      const data = await response.json();
      if (data?.pay_url) {
        paymentUrl.value = data.pay_url;
        try {
          window.location.href = data.pay_url;
        } catch (_) {}
      }
    } catch (err) {
      console.error("Ошибка Cryptocloud:", err);
    }
  };

  //Создаем ссылку для оплаты для легендарной подписки
  const activeLegendary = () => createCryptoCloudPayment("legend");

  //Переход на страницу активации PON
  const handlePonActivation = () => {
    if (isAuth.value) {
      emit("close");
      router.push({ name: PATHS_NAME.USER.SUBSCRIPTION_PURCHASE });
    } else {
      openAuth();
      sessionStorage.setItem("isPonPageMove", "true");
    }
  };

  //Создаем ссылку для оплаты для легендарной подписки
  const handleSubscriptionPayment = async () => {
    if (!isAuth.value) {
      openAuth();
      return;
    }
    if (selectedSubscription.value.tariff) {
      await createCryptoCloudPayment(selectedSubscription.value.tariff);
    }
  };

  //Открываем виджет авторизации
  const openAuth = () => {
    store.commit("AuthorizationSystem/toActiveSection", AUTH_SECTIONS_NAME.START_LOGIN);
    store.dispatch("Dropdowns/addNewDropdown", DROPDOWN_NAMES.auth);
  };


  //---------------------------------------------------------
  // HELPERS
  //---------------------------------------------------------
  //Helper: склонение месяцев  
  const pluralizeMonths = (n) => {
    if (n === 1) return "месяц";
    if (n >= 2 && n <= 4) return "месяца";
    return "месяцев";
  };
</script>

<style lang="scss" scoped>
@import "./styles.scss";
</style>